import {
  Button,
  Empty,
  MenuProps,
  Skeleton,
  Spin,
  Tooltip,
  Typography,
  message,
} from "antd";
import React, { useEffect } from "react";
import {
  EllipsisOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CalculatorOutlined,
  EditOutlined,
  FileSearchOutlined,
  ExportOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { RankingProjectCardMenuKeys } from "modules/organization/models/enums";
import { filterKebabMenuItems } from "modules/organization/utility/commonFunctions";
import { useRbac } from "auth/rbac/rbac";
import CustomDropdownMenu from "modules/shared/components/CustomDropdownMenu";
import CustomError from "modules/shared/components/CustomError";
import { getAvatar } from "modules/shared/utility";
import { Link, useNavigate } from "react-router-dom";
import { useOrganizationRoutes } from "routes/organizationRoute";
import { useProjectDetailsStore } from "modules/organization/store";
import { useRankingProjectListStore } from "modules/organization/store/rankingProjectListStore";
import { IRankingProjectCard } from "modules/organization/models/interface";
import { dateFormat } from "config/commonConfig";
import { mnDate } from "modules/shared/services";
import CopyRankingProject from "./CopyRankingProject";

const RankingProjectCard: React.FC<{
  rankingProject: IRankingProjectCard;
  onRun: (rankingProjectId: number) => void;
  onCopy: (rankingProject: IRankingProjectCard) => void;
}> = ({ rankingProject, onRun, onCopy }) => {
  const { hasPermissions } = useRbac();
  const navigate = useNavigate();
  const { organizationBaseRouteUrl } = useOrganizationRoutes();
  const { projectDetails } = useProjectDetailsStore();
  const { setRankingProjectDrawerState, setRankingProjectDrawerOptions } =
    useRankingProjectListStore();

  const _menuItems = [
    {
      label: "Equation Editor",
      key: RankingProjectCardMenuKeys.EQUATION_EDITOR,
      icon: <CalculatorOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Edit Ranking Project",
      key: RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT,
      icon: <EditOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Display Snapshot Result",
      key: RankingProjectCardMenuKeys.DISPLAY_SNAPSHOT_RESULT,
      icon: <FileSearchOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Export Ranks into Project",
      key: RankingProjectCardMenuKeys.EXPORT_RANKS,
      icon: <ExportOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Copy",
      key: RankingProjectCardMenuKeys.COPY,
      icon: <CopyOutlined role="img" aria-hidden />,
      allowedPermissions: [],
    },
    {
      label: "Delete",
      key: RankingProjectCardMenuKeys.DELETE,
      icon: <DeleteOutlined role="img" aria-hidden />,
      danger: true,
      allowedPermissions: [],
    },
  ];

  const onSelect = (e: any, rankingProject: IRankingProjectCard) => {
    const key = parseInt(e.key);
    const basePath =
      organizationBaseRouteUrl +
      `/project/${projectDetails?.slug}/ranking-projects/${rankingProject.id}`;
    if (key === RankingProjectCardMenuKeys.EQUATION_EDITOR) {
      navigate(basePath + "/equation-editor");
    } else if (key === RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT) {
      setRankingProjectDrawerOptions(
        RankingProjectCardMenuKeys.EDIT_RANKING_PROJECT,
        rankingProject,
      );
      setRankingProjectDrawerState(true);
    } else if (key === RankingProjectCardMenuKeys.DISPLAY_SNAPSHOT_RESULT) {
      navigate(basePath + "/snapshot");
    } else if (key === RankingProjectCardMenuKeys.EXPORT_RANKS) {
      message.info("Export Ranks into Project is not yet available.");
    } else if (key === RankingProjectCardMenuKeys.COPY) {
      onCopy(rankingProject);
    } else if (key === RankingProjectCardMenuKeys.DELETE) {
      message.info("Delete is not yet available.");
    }
  };

  const listActionItemsList: MenuProps["items"] = filterKebabMenuItems(
    _menuItems,
    hasPermissions,
    {
      project_role_id: projectDetails?.project_role_id,
    },
  );

  const isTemplateBased = rankingProject.ranking_type_id === 1;
  const cardTypeClassName = isTemplateBased
    ? "templateTypeCard"
    : "pointTypeCard";
  const cardClassName = rankingProject.is_running
    ? `activeCard ${cardTypeClassName}`
    : cardTypeClassName;
  const snapshotBasePath =
    organizationBaseRouteUrl +
    `/project/${projectDetails?.slug}/ranking-projects/${rankingProject.id}/snapshot`;

  return (
    <li className={cardClassName} key={rankingProject.id}>
      <div className="cardHead">
        <h3>
          <Typography.Text ellipsis={{ tooltip: rankingProject.title }}>
            {rankingProject.title}
          </Typography.Text>
        </h3>
        {rankingProject.created_by_user && (
          <Tooltip
            title={rankingProject.created_by_user.full_name}
            trigger={["hover", "focus"]}
          >
            <span className="userAvatar">
              {getAvatar(
                rankingProject.created_by_user.full_name,
                rankingProject.created_by_user.avatar_url,
              )}
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={rankingProject.is_running ? "Running" : "Run Ranking"}
          trigger={["hover", "focus"]}
        >
          <Button
            className="iconOnlyBtn smallIcon"
            shape="circle"
            aria-label={rankingProject.is_running ? "Running" : "Run Ranking"}
            onClick={() => onRun(rankingProject.id)}
          >
            {rankingProject.is_running ? (
              <PauseCircleOutlined role="img" aria-hidden />
            ) : (
              <PlayCircleOutlined role="img" aria-hidden />
            )}
          </Button>
        </Tooltip>
        <CustomDropdownMenu
          menuItems={listActionItemsList}
          onSelect={(e) => onSelect(e, rankingProject)}
          disabled={!listActionItemsList.some((item) => item?.key)}
        >
          <EllipsisOutlined role="img" aria-label="More" />
        </CustomDropdownMenu>
      </div>
      <div className="cardContent">
        <ul className="rankProjectStatsList">
          <li>
            <label>Last run date</label>
            <span>
              {rankingProject.last_execution
                ? mnDate(rankingProject.last_execution.executed_at).format(
                    dateFormat.tableDate,
                  )
                : "Not yet run"}
            </span>
          </li>
          {rankingProject.ranking_summary?.map((eachSummary) => {
            const labelAndCount = (
              <>
                {/* 2026-08-17: a rank/tier name can be an arbitrarily long value (e.g. pasted/
                    garbage input) — without a bounded width + tooltip, a long unbroken string
                    forces this flex item (and the whole card row) to grow far beyond the card,
                    breaking the page layout. Tooltip surfaces the full value on hover/focus. */}
                <Tooltip title={eachSummary.rank_name} trigger={["hover", "focus"]}>
                  <label className="rankStatLabel">{eachSummary.rank_name}</label>
                </Tooltip>
                <span className="errorColor">
                  {eachSummary.score ?? eachSummary.record_count}
                </span>
              </>
            );
            const snapshotLinkQuery = isTemplateBased
              ? `rank_filter=${encodeURIComponent(eachSummary.rank_name)}`
              : `score_filter=${eachSummary.score}`;
            return (
              <li key={eachSummary.rank_name}>
                {isTemplateBased || eachSummary.score !== null ? (
                  <Link
                    className="rankSummaryLink"
                    to={`${snapshotBasePath}?${snapshotLinkQuery}`}
                    aria-label={`View ${eachSummary.rank_name} records in Snapshot`}
                  >
                    {labelAndCount}
                  </Link>
                ) : (
                  labelAndCount
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
};

const RankingProjectCardSkeleton: React.FC<{}> = () => (
  <li className="templateTypeCard">
    <div className="cardHead">
      <h3>
        <Skeleton.Input active size="small" style={{ width: "60%" }} />
      </h3>
      <span className="userAvatar">
        <Skeleton.Avatar active size="small" shape="circle" />
      </span>
      <Skeleton.Avatar active size="small" shape="circle" />
      <Skeleton.Avatar
        active
        size="small"
        shape="circle"
        style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      />
    </div>
    <div className="cardContent">
      <ul className="rankProjectStatsList">
        {Array.from({ length: 2 }, (_, index) => (
          <li key={index}>
            <label>
              <Skeleton.Input active size="small" style={{ width: 70 }} />
            </label>
            <span>
              <Skeleton.Input active size="small" style={{ width: 30 }} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  </li>
);

const DEFAULT_SKELETON_CARD_COUNT = 5;

const RankingProjectCardList: React.FC<{}> = () => {
  const { projectDetails } = useProjectDetailsStore();
  const rankingProjectsLoader = useRankingProjectListStore(
    (state) => state.rankingProjectsLoader,
  );
  const dataFetchError = useRankingProjectListStore(
    (state) => state.dataFetchError,
  );
  const rankingProjects = useRankingProjectListStore(
    (state) => state.rankingProjects,
  );
  const refreshRankingProjectList = useRankingProjectListStore(
    (state) => state.refreshRankingProjectList,
  );
  const [copyingRankingProject, setCopyingRankingProject] =
    React.useState<IRankingProjectCard | null>(null);

  useEffect(() => {
    if (projectDetails?.slug) {
      refreshRankingProjectList(projectDetails.slug).catch((error) =>
        console.log(error),
      );
    }
  }, [projectDetails?.slug, refreshRankingProjectList]);

  const onRun = () => {
    message.info("Ranking execution engine is not yet implemented.");
  };

  const isFirstLoad = rankingProjectsLoader && rankingProjects === null;

  return (
    <Spin spinning={rankingProjectsLoader && !isFirstLoad}>
      <ul className="cardWrap rankingProjectCardWrap">
        {dataFetchError && (
          <CustomError
            errorDetails={dataFetchError}
            refreshAction={refreshRankingProjectList}
            refreshActionParams={projectDetails?.slug}
            reloadPage={false}
          />
        )}
        {!dataFetchError &&
          (isFirstLoad ? (
            Array.from({ length: DEFAULT_SKELETON_CARD_COUNT }, (_, index) => (
              <RankingProjectCardSkeleton key={`skeleton-${index}`} />
            ))
          ) : rankingProjects?.length ? (
            rankingProjects.map((eachRankingProject) => (
              <RankingProjectCard
                key={eachRankingProject.id}
                rankingProject={eachRankingProject}
                onRun={onRun}
                onCopy={setCopyingRankingProject}
              />
            ))
          ) : (
            <li className="noDataBlk">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </li>
          ))}
      </ul>
      <CopyRankingProject
        open={copyingRankingProject !== null}
        projectSlug={projectDetails?.slug}
        sourceRankingProjectId={copyingRankingProject?.id ?? null}
        sourceRankingProjectTitle={copyingRankingProject?.title ?? ""}
        onClose={() => setCopyingRankingProject(null)}
      />
    </Spin>
  );
};

export default RankingProjectCardList;
